import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ModuleRegistry, ValueFormatterParams } from '@ag-grid-community/core';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    MonitoringChannelDateRangesEnum,
    MonitoringChannelDrilldownExportType,
} from 'exports/interfaces/exports-monitoring-channel.interface';
import { ExportsMonitoringChannelDrilldownService } from 'exports/services/exports-monitoring-channel-drilldown.service';
import { ExportsMonitoringService } from 'exports/services/exports-monitoring.service';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DropdownFilterValue } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { UserApiService } from 'shared/services';
import { getCachedImage } from 'shared/util/get-cached-image';

@Component({
    selector: 'app-exports-monitoring-channel-drilldown',
    imports: [TranslateModule, AgGridModule, CommonModule],
    templateUrl: './exports-monitoring-channel-drilldown.component.html',
    styleUrl: './exports-monitoring-channel-drilldown.component.scss',
})
export class ExportsMonitoringChannelDrilldownComponent extends BaseGrid implements OnInit, OnDestroy {
    protected baseUrl = environment.BASE_URL.substring(0, environment.BASE_URL.length - 1);

    logoData = signal<string | null>(null);

    supplierNameColDef: ColDef = {
        headerName: 'Supplier Name',
        field: 'supplierName',
        width: 180,
        floatingFilter: true,
        unSortIcon: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        comparator: (valueA, valueB) => {
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        },
    };

    brandNameColDef: ColDef = {
        headerName: 'Brand Name',
        field: 'brandName',
        width: 180,
        floatingFilter: true,
        unSortIcon: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        comparator: (valueA, valueB) => {
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        },
    };

    public columnDefs: ColDef[] = [
        {
            headerName: 'Listing Name',
            field: 'listingName',
            width: 250,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            cellRenderer: 'linkCellRenderer',
            cellRendererParams: {
                links: [
                    {
                        computeUrl: (rowData: any) => `/listings/details/${rowData.listingId}/distributionTracking`,
                        computeDisabledState: (rowData: any) => [ListingStatusEnum.BULK_PROCESSING].includes(rowData.listingStatus),
                    },
                ],
            },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'exportDate',
            valueFormatter: this.dateFormatter,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            sort: 'desc',
            sortIndex: 0,
            filterParams: {
                values: [
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastDay'),
                        value: MonitoringChannelDateRangesEnum.LAST_DAY,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastWeek'),
                        value: MonitoringChannelDateRangesEnum.LAST_WEEK,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.dateRanges.lastMonth'),
                        value: MonitoringChannelDateRangesEnum.LAST_MONTH,
                    },
                ] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
        },
        {
            field: 'exportType',
            width: 150,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: [
                    {
                        label: this.translateService.instant('exports.monitoring.exportTypes.regular'),
                        value: MonitoringChannelDrilldownExportType.REGULAR_EXPORT,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.exportTypes.fullWithOverride'),
                        value: MonitoringChannelDrilldownExportType.FULL_EXPORT_WITH_OVERRIDE,
                    },
                    {
                        label: this.translateService.instant('exports.monitoring.exportTypes.fullWithoutOverride'),
                        value: MonitoringChannelDrilldownExportType.FULL_EXPORT_WITHOUT_OVERRIDE,
                    },
                ] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            valueFormatter: (params) => this.exportTypeValueFormatter(params, this.translateService),
        },
        {
            headerName: 'Percentage',
            field: 'successRate',
            cellRenderer: 'distributionTrackingStatusCellRenderer',
            enableValue: true,
            filter: 'agNumberColumnFilter',
            floatingFilter: true,
            floatingFilterComponent: 'rangeOptionsFloatingFilter',
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            filterParams: {
                ranges: [
                    { min: 0, max: 20, label: '0–20%' },
                    { min: 21, max: 40, label: '21–40%' },
                    { min: 41, max: 60, label: '41–60%' },
                    { min: 61, max: 80, label: '61–80%' },
                    { min: 81, max: 99, label: '81–99%' },
                    { min: 100, max: 100, label: '100%' },
                ],
                suppressAndOrCondition: true,
                defaultOption: 'inRange',
            },
        },
        {
            field: '',
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 70,
            maxWidth: 70,
            headerComponent: 'resetFiltersHeader',
        },
    ];

    constructor(
        public exportsMonitoringChannelDrilldownService: ExportsMonitoringChannelDrilldownService,
        public exportsMonitoringService: ExportsMonitoringService,
        private translateService: TranslateService,
        private userApiService: UserApiService,
        activatedRoute: ActivatedRoute,
        router: Router,
        destroyRef: DestroyRef,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(exportsMonitoringChannelDrilldownService, router, activatedRoute, destroyRef);
        effect(() => {
            const singularChannelData = this.exportsMonitoringService.singularChannelData();
            if (singularChannelData) {
                const logoUrl = singularChannelData.channelLogoUrl;
                if (logoUrl) {
                    this.setLogoUrl(logoUrl);
                }
            }
        });
    }

    ngOnInit(): void {
        this.activatedRoute.params.subscribe((params) => {
            const channelId = params['channelId'];
            if (channelId) {
                this.exportsMonitoringChannelDrilldownService.channelId.set(channelId);
                this.exportsMonitoringService.channelId.set(channelId);
            }
        });
        this.userApiService.getLoginInformation().subscribe((loginInfo) => {
            if (loginInfo.isShiji) {
                this.columnDefs = this.columnDefs.filter((colDef) => colDef.field !== 'brandName');
                this.columnDefs.unshift(this.supplierNameColDef);
            } else {
                this.columnDefs = this.columnDefs.filter((colDef) => colDef.field !== 'supplierName');
                this.columnDefs.unshift(this.brandNameColDef);
            }
        });
    }

    setLogoUrl(url: string): void {
        getCachedImage(this.baseUrl + url).then((data) => {
            this.logoData.set(data);
        });
    }

    goBack(): void {
        this.router.navigate(['..'], { relativeTo: this.activatedRoute });
    }

    private exportTypeValueFormatter(params: ValueFormatterParams, translateService: TranslateService) {
        let displayValue = '';

        switch (params.value) {
            case MonitoringChannelDrilldownExportType.REGULAR_EXPORT:
                displayValue = translateService !== undefined ? translateService.instant('exports.monitoring.exportTypes.regular') : '';
                break;
            case MonitoringChannelDrilldownExportType.FULL_EXPORT_WITHOUT_OVERRIDE:
                displayValue =
                    translateService !== undefined ? translateService.instant('exports.monitoring.exportTypes.fullWithoutOverride') : '';
                break;
            case MonitoringChannelDrilldownExportType.FULL_EXPORT_WITH_OVERRIDE:
                displayValue =
                    translateService !== undefined ? translateService.instant('exports.monitoring.exportTypes.fullWithOverride') : '';
                break;
        }

        return displayValue;
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.exportsMonitoringChannelDrilldownService.channelId.set(null);
        this.exportsMonitoringService.channelId.set(null);
    }
}
